import * as React from 'react'
import { useMutation } from '@apollo/react-hooks'

import {
  i18n,
  RateLimitBucket,
  useCaptcha,
  useHeaderHeight,
  useLogger,
  useRequestConfig,
} from '@thg-commerce/enterprise-core'
import { FormItem, Input, Modal } from '@thg-commerce/gravity-elements'
import { DynamicForm } from '@thg-commerce/gravity-patterns'

import { SendReferralEmail as SEND_REFERRAL_EMAIL_MUTATION } from '../../widgets/graphql/Mutation/SendReferralEmail.graphql'

import {
  FormContainer,
  FormWrapper,
  MessageContainer,
  ModalTitle,
  SubmitButton,
  SubmittedTitle,
  SuccessTick,
} from './styles'

export enum FormStatus {
  UNSUBMITTED = 'UNSUBMITTED',
  FAILURE = 'FAILURE',
  SUCCESSFUL = 'SUCCESSFUL',
}

interface SubmitValues {
  emailAddresses: string
}

interface DynamicReferralEmailModalProps {
  openModal: boolean
  onClose: () => void
}

export const DynamicReferralEmailModal = (
  props: DynamicReferralEmailModalProps,
) => {
  const logger = useLogger()
  const { csrf } = useRequestConfig()
  const headerHeight = useHeaderHeight()
  const captcha = useCaptcha({
    bucket: RateLimitBucket.REFERRAL_EMAIL,
  })

  const [formStatus, setFormStatus] = React.useState<FormStatus>(
    FormStatus.UNSUBMITTED,
  )

  React.useEffect(() => {
    setFormStatus(FormStatus.UNSUBMITTED)
  }, [props.openModal])

  const [sendReferralEmail] = useMutation<
    { sendReferralEmail: { errors: string } | undefined },
    {
      emailAddresses: string[]
    }
  >(SEND_REFERRAL_EMAIL_MUTATION)

  const i18nTextModal = {
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.arialabel'),
  }

  const i18nText = {
    modalTitle: i18n('account.referrals.sharing.email.modal.title'),
    emailLabel: i18n('account.referrals.sharing.email.modal.form.label'),
    emailPlaceholder: i18n(
      'account.referrals.sharing.email.modal.form.input.placeholder',
    ),
    submitButton: i18n('account.referrals.sharing.email.modal.send.text'),
    errorText: i18n('verificationtokenerror.invalid.title.text'),
    successMessage: i18n('account.referrals.sharing.email.success.message'),
  }

  const handleSendReferralEmail = (
    values: SubmitValues,
    captchaResponse?: string,
  ) => {
    const emailRegex = /^[a-zA-Z\d.!#$%&*+/=?^_`{|}~-]+@[a-zA-Z\d-]+(?:\.[a-zA-Z\d-]+)$/
    const emailAddressesTransformed = values.emailAddresses
      .split(/[\s,]+/)
      .filter((item) => item && emailRegex.test(item))

    try {
      sendReferralEmail({
        fetchPolicy: 'no-cache',
        variables: {
          emailAddresses: emailAddressesTransformed,
        },
        context: {
          headers: {
            'X-Captcha-Type': captcha.type,
            'X-Captcha-Response': captchaResponse,
          },
        },
      }).then(({ data, errors }) => {
        if (
          data?.sendReferralEmail &&
          !data.sendReferralEmail.errors &&
          !errors
        ) {
          setFormStatus(FormStatus.SUCCESSFUL)
        } else {
          logger.error('sendReferralEmail error in Send Referral Email Modal', {
            data,
            errors: data?.sendReferralEmail?.errors,
            'X-Captcha-Type': captcha.type,
            'X-Captcha-Response': captchaResponse,
          })
          setFormStatus(FormStatus.FAILURE)
        }
      })
    } catch (err) {
      logger.debug(err.message, {
        reqId: 'Send Referral Email Modal',
        'X-Captcha-Type': captcha.type,
        'X-Captcha-Response': captchaResponse,
      })

      setFormStatus(FormStatus.FAILURE)
    }
  }

  const onSubmitHandler = (fields: SubmitValues) => {
    captcha.submit((response) => handleSendReferralEmail(fields, response))
  }

  return (
    <Modal
      open={props.openModal}
      gridColSpan={[12, 12, 8, 6]}
      i18nText={i18nTextModal}
      contentPadding="0 0 0"
      headerOffset={headerHeight}
      onClose={props.onClose}
      showHeader={true}
    >
      <FormContainer>
        {formStatus === FormStatus.UNSUBMITTED ? (
          <React.Fragment>
            <ModalTitle data-testid="modal-title">
              {i18nText.modalTitle}
            </ModalTitle>
            <FormWrapper>
              <DynamicForm
                csrf={csrf}
                onSubmit={onSubmitHandler}
                data-testid="form-send-referral-email"
                fields={[
                  {
                    fieldName: 'emailAddresses',
                    renderField: (
                      ref: React.RefObject<HTMLInputElement>,
                      key: string,
                    ) => (
                      <FormItem bindref={ref} disableSpacing key={key}>
                        <Input
                          label={i18nText.emailLabel}
                          required
                          type="text"
                          placeholder={i18nText.emailPlaceholder}
                        />
                      </FormItem>
                    ),
                  },
                ]}
              >
                {captcha.captcha}
                <SubmitButton
                  type="submit"
                  emphasis="high"
                  data-testid="button-submit-referral-email"
                >
                  {i18nText.submitButton}
                </SubmitButton>
              </DynamicForm>
            </FormWrapper>
          </React.Fragment>
        ) : (
          [
            formStatus === FormStatus.SUCCESSFUL ? (
              <MessageContainer>
                <SuccessTick viewBox="0 0 14 14" />
                <SubmittedTitle data-testid="success-text">
                  {i18nText.successMessage}
                </SubmittedTitle>
              </MessageContainer>
            ) : (
              <MessageContainer>
                <SubmittedTitle data-testid="error-text">
                  {i18nText.errorText}
                </SubmittedTitle>
              </MessageContainer>
            ),
          ]
        )}
      </FormContainer>
    </Modal>
  )
}
